import React, { useRef, useState, useLayoutEffect } from 'react';

export const COVER_W = 500;
export const COVER_H = 800;

const ICONS: Record<string, string> = {
    heart: '♥', star: '★', sparkles: '✨', circle: '○', square: '□', triangle: '△',
    crown: '👑', wand: '🪄', castle: '🏰', rocket: '🚀', zap: '⚡', shield: '🛡️',
    cloud: '☁️', sun: '☀️', moon: '🌙', tree: '🌲', flower: '🌸', rainbow: '🌈',
    gift: '🎁', music: '🎵', camera: '📷', book: '📖', palette: '🎨', pencil: '✏️',
    award: '🏆', flag: '🚩', target: '🎯',
};

export interface CoverElement {
    id: string;
    type: 'text' | 'icon' | 'graphic' | 'sticker';
    content: string;
    x: number;
    y: number;
    fontSize?: number;
    fontFamily?: string;
    rotation?: number;
    scale?: number;
    color?: string;
    iconName?: string;
    imageUrl?: string;
    textStyle?: 'shadow' | 'outline' | 'glow' | 'none'; // NEW
    textCurve?: 'none' | 'up' | 'down';                  // NEW
}

export interface CoverBackground {
    type: 'solid' | 'gradient' | 'image' | 'pattern';
    value: string;
}

export interface CoverData {
    background?: CoverBackground;
    elements?: CoverElement[];
}

function getBackgroundStyle(bg?: CoverBackground): React.CSSProperties {
    if (!bg) return { background: '#ffffff' };
    switch (bg.type) {
        case 'solid':    return { background: bg.value };
        case 'gradient': return { background: bg.value };
        case 'image':    return { backgroundImage: `url(${bg.value})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' };
        case 'pattern':  return { background: '#6C5CE7', backgroundImage: bg.value };
        default:         return { background: '#ffffff' };
    }
}

// Matches getTextShadow in CoverDesigner exactly
function getTextShadow(style: string | undefined, color: string | undefined): string {
    switch (style) {
        case 'outline': return `-2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 2px 2px 0 #000`;
        case 'glow':    return `0 0 8px ${color ?? '#fff'}, 0 0 20px ${color ?? '#fff'}, 0 0 35px ${color ?? '#fff'}`;
        case 'none':    return 'none';
        default:        return '3px 3px 6px rgba(0,0,0,0.4)';
    }
}

// SVG curved text — shared by designer and renderer
export function CurvedText({ content, curve, fontSize, fontFamily, color, textStyle }: {
  content: string; curve: 'up' | 'down';
  fontSize: number; fontFamily: string; color: string;
  textStyle?: 'shadow' | 'outline' | 'glow' | 'none';
}) {
  const W = 460; const H = 140;
  const mid = H / 2;
  const bend = 70;
  const pathD = curve === 'up'
      ? `M 0,${mid + bend / 2} Q ${W / 2},${mid - bend} ${W},${mid + bend / 2}`
      : `M 0,${mid - bend / 2} Q ${W / 2},${mid + bend} ${W},${mid - bend / 2}`;
  const curveId = `curve-${curve}-${Math.random().toString(36).slice(2, 6)}`;
  const filterId = `filter-${curveId}`;

  const isOutline = textStyle === 'outline';
  const isGlow    = textStyle === 'glow';
  const isShadow  = !textStyle || textStyle === 'shadow';

  return (
      <svg width={W} height={H} style={{ overflow: 'visible', display: 'block' }}>
          <defs>
              <path id={curveId} d={pathD} />
              <filter id={filterId} x="-20%" y="-20%" width="140%" height="140%">
                  {isShadow && (
                      // drop shadow
                      <feDropShadow dx="3" dy="3" stdDeviation="3" floodColor="rgba(0,0,0,0.4)" />
                  )}
                  {isGlow && (
                      // multi-pass glow
                      <>
                          <feGaussianBlur stdDeviation="6" result="blur1" />
                          <feGaussianBlur stdDeviation="12" result="blur2" in="SourceGraphic" />
                          <feMerge>
                              <feMergeNode in="blur1" />
                              <feMergeNode in="blur2" />
                              <feMergeNode in="SourceGraphic" />
                          </feMerge>
                      </>
                  )}
              </filter>
          </defs>

          {/* Outline — rendered as a stroked copy behind the fill */}
          {isOutline && (
              <text fontFamily={fontFamily} fontSize={fontSize} fontWeight={800}
                  fill="none" stroke="#000" strokeWidth={4} textAnchor="middle">
                  <textPath href={`#${curveId}`} startOffset="50%">
                      {content}
                  </textPath>
              </text>
          )}

          {/* Main text */}
          <text fontFamily={fontFamily} fontSize={fontSize} fontWeight={800}
              fill={color} textAnchor="middle"
              filter={isShadow || isGlow ? `url(#${filterId})` : undefined}>
              <textPath href={`#${curveId}`} startOffset="50%">
                  {content}
              </textPath>
          </text>
      </svg>
  );
}

function renderElement(el: CoverElement): React.ReactNode {
    const positionedStyle: React.CSSProperties = {
        position: 'absolute',
        left: `${el.x}%`,
        top: `${el.y}%`,
        transform: `translate(-50%,-50%) rotate(${el.rotation ?? 0}deg) scale(${el.scale ?? 1})`,
        pointerEvents: 'none',
        userSelect: 'none',
        whiteSpace: 'nowrap',
    };

    if (el.type === 'graphic' && el.imageUrl) {
        return (
            <div key={el.id} style={positionedStyle}>
                <img src={el.imageUrl} alt={el.content} style={{
                    maxWidth: 200, maxHeight: 200,
                    width: 'auto', height: 'auto', display: 'block',
                    filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.2))',
                }} />
            </div>
        );
    }

    if (el.type === 'icon') {
        return (
            <div key={el.id} style={positionedStyle}>
                <span style={{ fontSize: el.fontSize ?? 48, color: el.color ?? '#6C5CE7', display: 'block' }}>
                    {ICONS[el.iconName ?? ''] ?? '✦'}
                </span>
            </div>
        );
    }

    if (el.type === 'text') {
        const shadow = getTextShadow(el.textStyle, el.color);

        // Curved text — center-anchored at el.x, el.y
        if (el.textCurve && el.textCurve !== 'none') {
            return (
                <div key={el.id} style={{ ...positionedStyle, whiteSpace: 'normal' }}>
                    <CurvedText
    content={el.content}
    curve={el.textCurve}
    fontSize={el.fontSize ?? 24}
    fontFamily={el.fontFamily ?? 'Inter'}
    color={el.color ?? '#FFFFFF'}
    textStyle={el.textStyle}  // ← was textShadow={shadow}
/>
                </div>
            );
        }

        // Straight text: full-width centered, wraps instead of running off the cover.
// el.x is unused here; text is always horizontally centered on the cover.
return (
    <div key={el.id} style={{
        position: 'absolute',
        left: 0,
        width: COVER_W,
        boxSizing: 'border-box',
        padding: '0 20px',
        top: `${el.y}%`,
        transform: 'translateY(-50%)',
        textAlign: 'center',
        whiteSpace: 'normal',
        overflowWrap: 'break-word',
        lineHeight: 1.15,
        fontSize: el.fontSize ?? 24,
        fontFamily: el.fontFamily ?? 'Inter',
        fontWeight: 800,
        color: el.color ?? '#FFFFFF',
        textShadow: shadow,
        pointerEvents: 'none',
        userSelect: 'none',
    }}>
        {el.content}
    </div>
);
    }

    return <div key={el.id} style={positionedStyle}>{el.content}</div>;
}

interface CoverRendererProps {
    coverData: CoverData;
    width?: number;
    borderRadius?: number | string;
    boxShadow?: string;
    style?: React.CSSProperties;
    className?: string;
}

const CoverRenderer: React.FC<CoverRendererProps> = ({
    coverData, width: widthProp, borderRadius, boxShadow, style, className,
}) => {
    const outerRef = useRef<HTMLDivElement>(null);
    const [measuredWidth, setMeasuredWidth] = useState<number>(widthProp ?? 0);

    useLayoutEffect(() => {
        if (widthProp !== undefined) return;
        const el = outerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => setMeasuredWidth(entry.contentRect.width));
        ro.observe(el);
        return () => ro.disconnect();
    }, [widthProp]);

    const effectiveWidth  = widthProp ?? measuredWidth;
    const scale           = effectiveWidth > 0 ? effectiveWidth / COVER_W : 1;
    const effectiveHeight = Math.round(effectiveWidth * (COVER_H / COVER_W));

    const outerStyle: React.CSSProperties = {
        width:       widthProp !== undefined ? effectiveWidth  : '100%',
        height:      widthProp !== undefined ? effectiveHeight : undefined,
        aspectRatio: widthProp !== undefined ? undefined       : '5/8',
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
        borderRadius,
        boxShadow,
        ...getBackgroundStyle(coverData.background),
        ...style,
    };

    const innerStyle: React.CSSProperties = {
        position: 'absolute', top: 0, left: 0,
        width: COVER_W, height: COVER_H,
        transformOrigin: 'top left',
        transform: effectiveWidth > 0 ? `scale(${scale})` : 'none',
    };

    return (
        <div ref={outerRef} style={outerStyle} className={className}>
            <div style={innerStyle}>
                {coverData.elements?.map(renderElement)}
            </div>
        </div>
    );
};

export default CoverRenderer;